/* eslint-disable lodash/prefer-lodash-method */
/* eslint-disable i18next/no-literal-string */
import React, { useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { ThemeProvider as StyledProvider } from 'styled-components';
import { ThemeProvider as MuiProvider } from '@material-ui/core/styles';
import { useObserver } from 'mobx-react-lite';

import { getStyledTheme, getMuiTheme, getColors, shadows, fonts } from 'src/theme';
import { ColorsProvider } from 'contexts/colors';
import useUI from 'hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import PublicEnv from 'shared/utils/public-env';
import themes from 'theme/demo';

import { GlobalFontStyles } from './global-styles';
import { useGetWebCustomizationSettings } from './theme.utils';

export function ThemeProvider(props) {
  const { children } = props;
  const cart = useCart();
  const useDispensaryHook = useDispensary();
  const UI = useUI();

  let {
    query: { cName },
  } = useRouter();

  const isEmbedded = useObserver(() => UI.isEmbedded);
  const isDutchieMain = useObserver(() => UI.isDutchieMain);
  const isKiosk = useObserver(() => UI.isKiosk);
  const dispensaryFromCart = useObserver(() => cart.dispensary);
  const dispensaryFromHook = useObserver(() => useDispensaryHook.dispensary);
  const dispensary = dispensaryFromCart || dispensaryFromHook;

  const customFontFamilyRef = useRef();

  const highPerformanceMode = useDispensaryFlag(`rollout.high-performance-kiosks`, dispensary?.id) && isKiosk;
  const getWebCustomizationSettings = useGetWebCustomizationSettings(dispensary);

  if (!cName && dispensary) {
    cName = dispensary.cName;
  }

  const shouldUseDemoSkins = PublicEnv.appEnv === 'demo' || PublicEnv.appEnv === 'development';

  // memoize the themes so they don't get regenerated
  const stores = useMemo(() => {
    // when we're outside of embedded or don't know what dispensary we're in
    // set theme to default and load from there...
    if (!shouldUseDemoSkins && !dispensary && isEmbedded) {
      // isEmbedded should always have a dispensary, so lets wait for one...
      return null;
    }

    if (!dispensary && isKiosk) {
      // wait for dispensary in kiosk mode too, we need this for high performance mode to *always* work
      return null;
    }

    if (!shouldUseDemoSkins || !isEmbedded || !cName) {
      cName = `default`;
    }

    const theme = themes[cName] || themes.default;
    const colors = { ...getColors(), ...theme.colors };

    colors.linkColor = colors.primaryBlue;

    if (!isDutchieMain) {
      const { linkColor, navBarColor, fontFamily } = getWebCustomizationSettings();

      colors.linkColor = linkColor?.background ?? colors.linkColor;
      colors.primaryBlue = linkColor?.background ?? colors.primaryBlue;
      colors.menuBar = navBarColor?.background ?? colors.menu;

      /**
       * Checks if font family has been set and makes sure it is not already included in the list of fonts (which is possible on refresh/rerenders).
       * If these check out, we unshift (add the font to the front of the array).
       */
      customFontFamilyRef.current = fontFamily;
    }

    const { fontFamily: customFontFamily, fallbacks } = customFontFamilyRef.current ?? {};
    const dutchieFonts = !customFontFamily ? fonts : [customFontFamily, ...fallbacks];

    const embeddedMenu = { height: theme.banner?.height || 0 };
    const muiTheme = getMuiTheme({ colors, fonts: dutchieFonts, uiVariant: UI.variant, highPerformanceMode });
    const styledTheme = getStyledTheme({
      colors,
      embeddedMenu,
      fonts: dutchieFonts,
      shadows,
      breakpoints: muiTheme.breakpoints,
      highPerformanceMode,
    });

    return {
      theme,
      colors,
      styledTheme,
      muiTheme,
    };
  }, [cName, isEmbedded, getWebCustomizationSettings]);

  if (!stores) {
    return null;
  }

  const customFontFamilyEncoded = customFontFamilyRef.current?.fontFamilyEncoded;

  return (
    <React.Fragment>
      <ColorsProvider value={stores.colors}>
        <StyledProvider theme={stores.styledTheme}>
          <GlobalFontStyles />
          {!isDutchieMain && customFontFamilyEncoded && (
            <>
              <link rel='preconnect' href='https://fonts.googleapis.com' />
              <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin />
              <link
                id='customFont'
                rel='stylesheet'
                href={`https://fonts.googleapis.com/css2?family=${customFontFamilyEncoded}&display=swap`}
              />
            </>
          )}
          <MuiProvider theme={stores.muiTheme}>{children}</MuiProvider>
        </StyledProvider>
      </ColorsProvider>
    </React.Fragment>
  );
}
